import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {GetAnalysisChart, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {getDefaultVisualizationConfigGQL} from 'analysis-tool/ui/charting/getDefaultVisualizationConfig';
import DashboardLayout, {BlockSpec} from 'analysis-tool/ui/components/DashboardLayout';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {ChartNode} from 'charts/ChartTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import {DEFAULT_DATA_FRAME_ROW_LIMIT, GET_SUBSET_DATA_FRAME_QUERY} from 'data-frames/graphql/GetDataFrame';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import ToggleExpandButton from 'ui-library/buttons/ToggleExpandButton';
import {Header1} from 'ui-library/Headers';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

const BLOCK_HEIGHT = 400;
const NUM_SUBPLOTS_PER_ROW = 3;

const STEP_DATA_NAME_TO_CHART_NAME: Record<string, Array<string>> = {
  'Distribution histograms': ['distribution_histogram'],
  'Correlation charts between X': ['correlation_chart_between_x', 'correlation_coefficient_heatmap'],
  'Correlation charts between X and Y': ['correlation_chart_between_x_and_y'],
};

const CHART_NAME_TO_CHART_TITLE: Record<string, string> = {
  distribution_histogram: 'Distribution histogram',
  correlation_chart_between_x_and_y: 'Correlation chart between X and Y',
  correlation_chart_between_x: 'Correlation chart between X',
  correlation_coefficient_heatmap: 'Correlation coefficient heatmap',
};

const SUMMARY_TABLE_DATA_NAMES = ['Continuous summary table', 'Categorical summary table'];

const expandedBlockState = makeUseGlobalClientState<string | null>(null);

/**
 * Given a step, a step data name and a chart name, find the chart with the given name in the step data. If the
 * step data doesn't exist or the chart doesn't exist in the step data, return null.
 */
function findStepChartByName(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  chartName: string
): GetAnalysisChart | null {
  return (
    step.outputStepDatas
      .find(({name}) => name === stepDataName)
      ?.charts.map((chart) => assertIsAccessible(chart))
      .find((chart) => chart.name === chartName) ?? null
  );
}

function getSummaryTableBlock(
  dataFrameId: string,
  summaryTableDataName: string,
  expandedBlock: string | null,
  setExpandedBlock: (block: string | null) => void
): JSX.Element {
  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => {
        assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
        return (
          <PresentationalUniversalBlockView
            actionOrigin={ActionOrigin.MACHINE_LEARNING}
            inputDataFrame={dataFrame.dataFrameJson}
            paneProps={{
              title: summaryTableDataName,
              toolbar: (
                <ToggleExpandButton
                  isExpanded={expandedBlock === summaryTableDataName}
                  onChange={(newIsExpanded) => {
                    setExpandedBlock(newIsExpanded ? summaryTableDataName : null);
                  }}
                />
              ),
            }}
            visualizationConfig={getDefaultVisualizationConfigGQL(
              dataFrame.dataFrameJson.columns,
              VisualizationType.TABLE
            )}
            viewStateId={`eda-summary-table-${dataFrameId}`}
          />
        );
      }}
    </BenchlingQuery>
  );
}

function getEDAChartBlock(
  chart: ChartNode,
  expandedBlock: string | null,
  setExpandedBlock: (block: string | null) => void
): JSX.Element {
  return (
    <PresentationalUniversalBlockView
      actionOrigin={ActionOrigin.MACHINE_LEARNING}
      inputDataFrame={{
        name: 'EDA',
        columns: [],
        rows: [],
        hydratedProperties: {apiIdentifiedObjects: {}, blobDicts: {}},
      }}
      paneProps={{
        title: CHART_NAME_TO_CHART_TITLE[chart.name],
        toolbar: (
          <ToggleExpandButton
            isExpanded={expandedBlock === chart.name}
            onChange={(newIsExpanded) => {
              setExpandedBlock(newIsExpanded ? chart.name : null);
            }}
          />
        ),
      }}
      viewStateId={`eda-chart-${chart.id}`}
      // The visualization config is a placeholder as the chart is rendered from the chart node.
      visualizationConfig={getDefaultVisualizationConfigGQL([], VisualizationType.TABLE)}
      renderFromChart={true}
      chart={chart}
    />
  );
}

export default function ExploratoryDataAnalysisDashboardView({
  step,
}: MainViewComponentProps<AnalysisMainViewItemType.EXPLORATORY_DATA_ANALYSIS>): JSX.Element {
  const [expandedBlock, setExpandedBlock] = expandedBlockState.useGlobalClientState(step.id);

  const blockSpecsMap: Record<string, BlockSpec> = {} as Record<string, BlockSpec>;

  const stepDataNameToAvailableChartName: Record<string, Array<string>> = {};
  Object.entries(STEP_DATA_NAME_TO_CHART_NAME).forEach(([stepDataName, chartNames]) => {
    const availableCharts = chartNames
      .map((chartName) => findStepChartByName(step, stepDataName, chartName))
      .filter((chart): chart is ChartNode => chart != null);

    if (availableCharts.length > 0) {
      stepDataNameToAvailableChartName[stepDataName] = availableCharts.map((chart) => chart.name);
    }

    availableCharts.forEach((chart) => {
      // The block height is calculated based on the number of rows of subplots in chart. The height of each
      // row is BLOCK_HEIGHT / 2.
      const blockHeight =
        (BLOCK_HEIGHT / 2) * Math.ceil((chart.libraryConfig.data?.length || 1) / NUM_SUBPLOTS_PER_ROW) +
        BLOCK_HEIGHT / 2;

      blockSpecsMap[chart.name] = {
        minHeight: blockHeight,
        maxHeight: blockHeight,
        key: chart.name,
        block: getEDAChartBlock(chart, expandedBlock, setExpandedBlock),
      };
    });
  });

  const chartSections = Object.entries(stepDataNameToAvailableChartName).map(
    ([stepDataName, availableChartNames]) => (
      <>
        <Header1 className={css(styles.header, styles.firstHeader)}>{stepDataName}</Header1>
        {availableChartNames.map((chartName) => (
          <DashboardLayout key={chartName} blockSpecs={[blockSpecsMap[chartName]]} />
        ))}
      </>
    )
  );

  SUMMARY_TABLE_DATA_NAMES.forEach((summaryTableDataName) => {
    const dataFrameId = findAndAssertSingleStepDataFrameId(step, summaryTableDataName);
    blockSpecsMap[summaryTableDataName] = {
      minHeight: BLOCK_HEIGHT / 2,
      maxHeight: BLOCK_HEIGHT,
      key: summaryTableDataName,
      block: getSummaryTableBlock(dataFrameId, summaryTableDataName, expandedBlock, setExpandedBlock),
    };
  });

  return (
    <div className={css(styles.stepWrapper)}>
      {expandedBlock == null ? (
        <>
          <Header1 className={css(styles.header, styles.firstHeader)}>Summary tables</Header1>
          <DashboardLayout
            blockSpecs={SUMMARY_TABLE_DATA_NAMES.map((tableName) => blockSpecsMap[tableName])}
          />
          {chartSections.map((section, index) => (
            <div key={`eda-chart-section-${index}`}>{section}</div>
          ))}
        </>
      ) : (
        <DashboardLayout blockSpecs={[blockSpecsMap[expandedBlock]]} expandedBlockKey={expandedBlock} />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  header: {
    marginLeft: ANALYSIS_TOOL_SPACING,
  },
  firstHeader: {
    marginTop: ANALYSIS_TOOL_SPACING,
  },
  stepWrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});
